import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { verifyEmailUpdateAPI } from "../../api/auth";
import useToast from "../../hooks/useToast";
import { useTranslation } from "react-i18next";
import { openModal, closeModal } from "../../redux/slices/uiSlice";
import { useDispatch } from "react-redux";
import { updateUser } from "../../redux/slices/userSlice";
import { clearEmailVerification } from "../../redux/slices/authSlice";
import getUser from "../../api/user";
import { setUser } from "../../redux/slices/userSlice";

const VerifyEmailUpdatePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [isVerified, setIsVerified] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const token = searchParams.get("token");

  const { mutate: verifyEmail } = useMutation({
    mutationFn: verifyEmailUpdateAPI,
    onSuccess: async (data) => {
      setIsVerified(true);
      setIsVerifying(false);

      // Check if user has a token before trying to fetch user data
      // If no token, just update verification status manually to avoid 401 redirect
      const authToken = localStorage.getItem("token") || sessionStorage.getItem("token");
      const isValidToken = authToken && authToken !== "undefined" && authToken !== "null" && authToken.trim() !== "";

      if (isValidToken) {
        // Only fetch user data if user is authenticated
        try {
          const userData = await getUser();
          dispatch(setUser(userData as any));
        } catch (error: any) {
          console.error("Error refreshing user data:", error);
          // If getUser fails, just update verification status manually
          // Don't let the error propagate to avoid axios interceptor redirect
          dispatch(updateUser({ is_email_verified: true }));
        }
      } else {
        // No token - just update verification status manually
        dispatch(updateUser({ is_email_verified: true }));
      }

      // Clear email verification state
      dispatch(clearEmailVerification());
      // Don't close modals or navigate automatically - let user stay on success page
      // User will click "Go to Login" button when ready
      useToast(t("verifyEmail.verificationSuccess") || "Your email has been successfully verified.", "success");
    },
    onError: (error: Error) => {
      setIsVerified(false);
      setIsVerifying(false);
      useToast(error.message || t("verifyEmail.verificationFailed") || "Email verification failed.", "error");
    },
  });

  useEffect(() => {
    if (!token) {
      useToast(t("verifyEmail.invalidToken") || "Invalid verification token.", "error");
      setIsVerifying(false);
      return;
    }

    // Call verify email API
    verifyEmail(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleGoToLogin = () => {
    dispatch(openModal("login"));
    navigate("/");
  };

  if (isVerifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-primary-ivory lg:p-[2vw] p-4">
        <div className="bg-white-500 2xl:rounded-[2.083vw] lg:rounded-[1.5vw] rounded-3xl lg:p-[2vw] md:p-7 p-4 2xl:max-w-[26vw] max-w-100 w-full text-center">
          <div className="flex flex-col items-center justify-center">
            <svg
              className="animate-spin lg:w-[3vw] w-12 lg:h-[3vw] h-12 text-primary-orange mb-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <p className="lg:text-[1.1vw]/[1.4vw] text-base/tight text-black-800 font-bold">
              {t("verifyEmail.verifying") || "Verifying your email..."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-primary-ivory lg:p-[2vw] p-4">
      <div className="bg-white-500 2xl:rounded-[2.083vw] lg:rounded-[1.5vw] rounded-3xl lg:p-[2vw] md:p-7 p-4 2xl:max-w-[26vw] max-w-100 w-full text-center">
        {isVerified ? (
          <>
            {/* Success Icon */}
            <div className="flex justify-center mb-4">
              <svg
                className="lg:w-[5vw] w-16 lg:h-[5vw] h-16 text-green-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>

            {/* Success Message */}
            <h1 className="lg:text-[1.9vw]/[2.2vw] text-2xl/6 font-semibold text-primary-orange lg:mb-[0.6vw] mb-3">
              {t("verifyEmail.emailVerified") || "Email Verified!"}
            </h1>
            <p className="lg:text-[1.1vw]/[1.4vw] text-base/tight text-black-800 font-bold lg:mb-[1.1vw] mb-4">
              {t("verifyEmail.verificationSuccess") || "Your email has been successfully verified."}
            </p>

            {/* Go to Login Button */}
            {/* <button
              onClick={handleGoToLogin}
              className="w-full 2xl:rounded-[0.26vw] rounded-[5px] h-full flex
    skew-x-[-10deg]
    2xl:border-[0.104vw] border-[1.5px] lg:text-[1.1vw]/[1.4vw] md:text-lg/tight text-base/tight font-bold text-primary-yellow bg-primary-yellow border-black-300 transition-all duration-200 cursor-pointer text-center 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]"
            >
              <span className="2xl:pt-[0.7vw] pt-2.5 2xl:m-[0.208vw] m-1 !mt-0 2xl:py-[0.5vw] py-2 lg:px-[5%] px-4 2xl:rounded-[0.26vw] rounded-[5px] skew-x-[0deg] !border-t-0 2xl:border-[0.104vw] border-[1.5px] border-black-300 bg-[linear-gradient(180deg,#D76A4D_0%,#B35840_101.89%)] w-full h-full">
                <span className="skew-x-[10deg] block">
                  {t("verifyEmail.goToLogin") || "Go to Login"}
                </span>
              </span>
            </button> */}
          </>
        ) : (
          <>
            {/* Error Icon */}
            <div className="flex justify-center mb-4">
              <svg
                className="lg:w-[5vw] w-16 lg:h-[5vw] h-16 text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>

            {/* Error Message */}
            <h1 className="lg:text-[1.9vw]/[2.2vw] text-2xl/6 font-semibold text-red-500 lg:mb-[0.6vw] mb-3">
              {t("verifyEmail.verificationFailed") || "Verification Failed"}
            </h1>
            <p className="lg:text-[1.1vw]/[1.4vw] text-base/tight text-black-800 font-bold lg:mb-[1.1vw] mb-4">
              {t("verifyEmail.invalidOrExpired") || "The verification link is invalid or has expired."}
            </p>

            {/* Go to Login Button */}
            {/* <button
              onClick={handleGoToLogin}
              className="w-full 2xl:rounded-[0.26vw] rounded-[5px] h-full flex
    skew-x-[-10deg]
    2xl:border-[0.104vw] border-[1.5px] lg:text-[1.1vw]/[1.4vw] md:text-lg/tight text-base/tight font-bold text-primary-yellow bg-primary-yellow border-black-300 transition-all duration-200 cursor-pointer text-center 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]"
            >
              <span className="2xl:pt-[0.7vw] pt-2.5 2xl:m-[0.208vw] m-1 !mt-0 2xl:py-[0.5vw] py-2 lg:px-[5%] px-4 2xl:rounded-[0.26vw] rounded-[5px] skew-x-[0deg] !border-t-0 2xl:border-[0.104vw] border-[1.5px] border-black-300 bg-[linear-gradient(180deg,#D76A4D_0%,#B35840_101.89%)] w-full h-full">
                <span className="skew-x-[10deg] block">
                  {t("verifyEmail.goToLogin") || "Go to Login"}
                </span>
              </span>
            </button> */}
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyEmailUpdatePage;
